import { useEffect, useState } from "react";
import Button from "./ui/Button.jsx";
import {
  mapEntraConnectApiError,
  validateEntraConnectFields,
} from "./onboarding/entraConnectValidation.js";
import { ENTRA_M2M_GUIDANCE } from "./onboarding/entraConnectGuidance.js";
import { getProjectTenantDisplayUrl } from "../utils/projectProvider.js";

/**
 * Credential-update surface for existing Entra projects (POST-ARC.8).
 * Primary project creation uses ConnectEntraPanel in NewProjectModal.
 */

/**
 * @param {{ open: boolean, onClose: () => void, project: { projectId?: string, org?: { url?: string } } | null, onConnected?: () => void }} props
 */
export default function ConnectEntraModal({ open, onClose, project, onConnected }) {
  const projectId = project?.projectId;
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState(null);
  const [testOk, setTestOk] = useState(false);
  const [tenantDisplayName, setTenantDisplayName] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [connectBusy, setConnectBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTenantId(getProjectTenantDisplayUrl(project) || "");
    setClientId("");
    setClientSecret("");
    setError(null);
    setTestOk(false);
    setTenantDisplayName("");
  }, [open, project]);

  if (!open) return null;

  const bodyPayload = () => ({
    projectId,
    tenant_id: tenantId.trim(),
    client_id: clientId.trim(),
    client_secret: clientSecret.trim(),
  });

  const handleTest = async () => {
    setError(null);
    setTestOk(false);
    setTenantDisplayName("");
    const validated = validateEntraConnectFields({
      tenant_id: tenantId,
      client_id: clientId,
      client_secret: clientSecret,
    });
    if (!validated.ok) {
      setError(validated.error);
      return;
    }
    if (!projectId) {
      setError("Missing project.");
      return;
    }
    setTestBusy(true);
    try {
      const res = await fetch("/api/idp/entra/test-connection", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bodyPayload()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        setError(mapEntraConnectApiError(data));
        return;
      }
      setTestOk(true);
      setTenantDisplayName(String(data.tenant_display_name || validated.tenant_id));
    } catch (e) {
      setError(e?.message || "Test connection failed");
    } finally {
      setTestBusy(false);
    }
  };

  const handleConnect = async () => {
    if (!testOk || !projectId) return;
    setError(null);
    setConnectBusy(true);
    try {
      const res = await fetch("/api/idp/entra/connect", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bodyPayload()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        throw new Error(mapEntraConnectApiError(data));
      }
      onConnected?.();
      onClose?.();
    } catch (e) {
      setError(e?.message || "Connect failed");
    } finally {
      setConnectBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(5, 8, 16, 0.72)",
        display: "grid",
        placeItems: "center",
        padding: "24px",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="connect-entra-title"
    >
      <div
        style={{
          width: "100%",
          maxWidth: "520px",
          maxHeight: "90vh",
          overflow: "auto",
          background: "var(--bg-surface)",
          border: "1px solid var(--border)",
          borderRadius: "10px",
          padding: "24px",
        }}
      >
        <h2 id="connect-entra-title" style={{ fontSize: "18px", fontWeight: 600, marginBottom: "8px" }}>
          Update Entra credentials
        </h2>
        <p className="text-secondary" style={{ fontSize: "12px", marginBottom: "16px", whiteSpace: "pre-line" }}>
          {ENTRA_M2M_GUIDANCE}
        </p>

        <div style={{ display: "grid", gap: "12px", marginBottom: "16px" }}>
          <label style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
            <span className="text-secondary">Directory (tenant) ID</span>
            <input
              type="text"
              value={tenantId}
              onChange={(e) => {
                setTenantId(e.target.value);
                setTestOk(false);
              }}
              placeholder="00000000-0000-0000-0000-000000000000"
              style={fieldStyle}
            />
          </label>
          <label style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
            <span className="text-secondary">Application (client) ID</span>
            <input
              type="text"
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setTestOk(false);
              }}
              autoComplete="off"
              style={fieldStyle}
            />
          </label>
          <label style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
            <span className="text-secondary">Client secret</span>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type={showSecret ? "text" : "password"}
                value={clientSecret}
                onChange={(e) => {
                  setClientSecret(e.target.value);
                  setTestOk(false);
                }}
                autoComplete="off"
                style={{ ...fieldStyle, flex: 1 }}
              />
              <button type="button" onClick={() => setShowSecret((v) => !v)} style={{ fontSize: "12px", padding: "0 10px" }}>
                {showSecret ? "Hide" : "Show"}
              </button>
            </div>
          </label>
        </div>

        {testOk ? (
          <p style={{ fontSize: "13px", color: "var(--status-ok, #4ade80)", marginBottom: "8px" }}>
            ✓ Connection verified — {tenantDisplayName || "tenant reachable"}
          </p>
        ) : null}

        {error ? (
          <p style={{ fontSize: "13px", color: "var(--status-error, #f87171)", marginBottom: "12px" }}>{error}</p>
        ) : null}

        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="secondary" disabled={testBusy} onClick={() => void handleTest()}>
            {testBusy ? "Testing…" : "Test Connection"}
          </Button>
          <Button type="button" disabled={!testOk || connectBusy} onClick={() => void handleConnect()}>
            {connectBusy ? "Saving…" : "Save credentials"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const fieldStyle = {
  padding: "10px 12px",
  borderRadius: "6px",
  border: "1px solid var(--border)",
  color: "var(--text-primary)",
  background: "var(--bg-input)",
};
